import React, { useState, useEffect } from 'react';
import { Edit3, Check, X } from 'lucide-react';

const EditableField = ({ 
  label, 
  value, 
  originalValue, 
  type = 'text', 
  onChange, 
  isEditing, 
  onEditToggle,
  placeholder,
  required = false,
  options = null // For select fields
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalValue(value || '');
    setHasChanges(false);
  }, [value, isEditing]);

  const handleLocalChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    setHasChanges(newValue !== value);
  };

  const handleSave = () => {
    if (hasChanges) {
      onChange(localValue);
    }
    onEditToggle(false);
  };

  const handleCancel = () => {
    setLocalValue(value || '');
    setHasChanges(false);
    onEditToggle(false);
  };

  const isEdited = originalValue !== null && originalValue !== undefined && originalValue !== value;

  const renderInput = () => {
    const commonProps = {
      value: localValue,
      onChange: handleLocalChange,
      className: "w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500",
      placeholder: placeholder || `Enter ${label.toLowerCase()}`,
      required
    };

    if (options) {
      return (
        <select {...commonProps}>
          <option value="">Select {label}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    switch (type) {
      case 'email':
        return <input {...commonProps} type="email" />;
      case 'tel':
        return <input {...commonProps} type="tel" />;
      case 'date':
        return <input {...commonProps} type="date" />;
      case 'number':
        return <input {...commonProps} type="number" step="0.01" min="0" />;
      case 'textarea':
        return <textarea {...commonProps} rows={3} />;
      default:
        return <input {...commonProps} type="text" />;
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {isEdited && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Edited
            </span>
          )}
        </label>
        {!isEditing && (
          <button
            onClick={() => onEditToggle(true)}
            className="text-gray-400 hover:text-blue-600 transition-colors"
            title={`Edit ${label}`}
          >
            <Edit3 className="w-4 h-4" />
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-2">
          {renderInput()}
          <div className="flex justify-end space-x-2">
            <button
              onClick={handleCancel}
              className="px-2 py-1 text-sm text-gray-600 hover:text-gray-800 flex items-center"
            >
              <X className="w-3 h-3 mr-1" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <Check className="w-3 h-3 mr-1" />
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <div className="text-sm text-gray-900 min-h-[20px] py-1">
            {value || <span className="text-gray-400 italic">Not provided</span>}
          </div>
          {isEdited && originalValue && (
            <div className="text-xs text-gray-500">
              <span className="font-medium">Original:</span> {originalValue}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableField;
